/**
 * Canonical schema.org LocalBusiness data for Click One MSP.
 *
 * This is rendered into the homepage <Helmet> as JSON-LD for
 * JS-enabled crawlers (Google, Bing) and is what unlocks the local
 * knowledge panel + sitelinks search box in SERPs.
 *
 * NOTE: For the local knowledge panel to actually surface, the
 * `address` here must match a verified Google Business Profile
 * for Click One MSP. Without GBP verification, Google will use
 * this data for entity disambiguation but won't render the panel.
 */

const SITE_URL = 'https://clickonemsp.ca';

// Lower Mainland service area centered on downtown Vancouver
const VANCOUVER_GEO = { latitude: 49.2827, longitude: -123.1207 };

const CITIES_SERVED = [
  'Vancouver', 'Burnaby', 'Surrey', 'Richmond',
  'Coquitlam', 'Port Coquitlam', 'Port Moody', 'New Westminster',
  'North Vancouver', 'West Vancouver', 'Delta', 'Langley',
  'Maple Ridge', 'Pitt Meadows', 'White Rock', 'Abbotsford',
  'Mission', 'Chilliwack',
];

const SERVICES_OFFERED = [
  { name: 'Managed IT Services', url: `${SITE_URL}/managed-it` },
  { name: 'Cybersecurity Services', url: `${SITE_URL}/cybersecurity` },
  { name: 'Microsoft 365 Support', url: `${SITE_URL}/microsoft-365-support` },
  { name: 'Network Infrastructure', url: `${SITE_URL}/network` },
  { name: 'IT Strategy & Consulting', url: `${SITE_URL}/it-strategy` },
  { name: 'Compliance Services', url: `${SITE_URL}/compliance` },
  { name: 'Help Desk Support', url: `${SITE_URL}/help-desk` },
];

export const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': ['LocalBusiness', 'ProfessionalService'],
  '@id': `${SITE_URL}/#localbusiness`,
  name: 'Click One MSP',
  alternateName: 'ClickOneMSP',
  legalName: 'Click One MSP',
  url: SITE_URL,
  logo: `${SITE_URL}/clickonemsp-logo.png`,
  image: [
    `${SITE_URL}/clickonemsp-logo.png`,
    `${SITE_URL}/clickonemsp-og.png`,
  ],
  description: 'Click One MSP delivers managed IT, cybersecurity, and Microsoft 365 support for small and mid-sized Canadian businesses — flat-rate pricing, 24/7 Canadian help desk, and same-day on-site response across Metro Vancouver and the Fraser Valley.',
  slogan: 'Next-generation managed IT for Canadian businesses.',
  foundingDate: '2018',
  founder: { '@type': 'Person', name: 'Click One MSP Leadership Team' },
  telephone: '+1-778-562-6930',
  email: 'Sales@clickonemsp.ca',
  priceRange: '$$',
  paymentAccepted: 'Credit Card, Cash, Cheque, EFT, Wire Transfer',
  currenciesAccepted: 'CAD, USD',
  knowsLanguage: ['en-CA', 'en-US'],
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Vancouver',
    addressRegion: 'BC',
    addressCountry: 'CA',
    postalCode: 'V6B',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: VANCOUVER_GEO.latitude,
    longitude: VANCOUVER_GEO.longitude,
  },
  hasMap: 'https://www.google.com/maps/place/Vancouver,+BC',
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '08:00',
      closes: '18:00',
    },
    {
      '@type': 'OpeningHoursSpecification',
      description: '24/7 emergency help desk available for managed clients',
      dayOfWeek: ['Saturday', 'Sunday'],
      opens: '00:00',
      closes: '23:59',
    },
  ],
  areaServed: CITIES_SERVED.map(name => ({
    '@type': 'City',
    name,
    containedInPlace: { '@type': 'AdministrativeArea', name: 'British Columbia' },
  })),
  serviceArea: {
    '@type': 'GeoCircle',
    geoMidpoint: {
      '@type': 'GeoCoordinates',
      latitude: VANCOUVER_GEO.latitude,
      longitude: VANCOUVER_GEO.longitude,
    },
    geoRadius: '75000', // 75 km — covers Metro Vancouver + Fraser Valley
  },
  knowsAbout: [
    'Managed IT Services',
    'Cybersecurity',
    'Microsoft 365 Administration',
    'Network Infrastructure',
    'IT Strategy and vCIO',
    'PIPEDA Compliance',
    'SOC 2 Type II Readiness',
    'PCI DSS Compliance',
    'HIPAA Compliance',
    'Ransomware Protection',
    'Endpoint Detection and Response',
    'Microsoft Defender',
    'Azure Cloud Services',
    'Help Desk Support',
    'Disaster Recovery and Backup',
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'IT Services',
    itemListElement: SERVICES_OFFERED.map(s => ({
      '@type': 'Offer',
      itemOffered: { '@type': 'Service', name: s.name, url: s.url },
    })),
  },
  sameAs: [
    'https://www.linkedin.com/company/clickonemsp',
    'https://www.instagram.com/clickonemsp',
    'https://www.facebook.com/clickonemsp',
    'https://m.youtube.com/@Clickonemsp',
    'https://x.com/clickonemsp',
  ],
  contactPoint: [
    {
      '@type': 'ContactPoint',
      telephone: '+1-778-562-6930',
      contactType: 'sales',
      areaServed: 'CA',
      availableLanguage: ['en-CA', 'en-US'],
      contactOption: 'TollFree',
    },
    {
      '@type': 'ContactPoint',
      telephone: '+1-778-562-6930',
      contactType: 'technical support',
      areaServed: 'CA',
      availableLanguage: ['en-CA', 'en-US'],
      hoursAvailable: {
        '@type': 'OpeningHoursSpecification',
        description: '24/7 emergency help desk for managed clients',
      },
    },
    {
      '@type': 'ContactPoint',
      email: 'Sales@clickonemsp.ca',
      contactType: 'customer service',
      areaServed: 'CA',
    },
  ],
};

export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  name: 'Click One MSP',
  url: SITE_URL,
  description: 'Managed IT Services, Cybersecurity, and Microsoft 365 Support for Canadian Businesses',
  publisher: { '@id': `${SITE_URL}/#localbusiness` },
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_URL}/blog?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
  inLanguage: 'en-CA',
};

export default localBusinessSchema;
